/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.feature.calculators.wizard

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kpt.core.base.store.submit.SubmitOutbox
import kpt.core.base.ui.viewmodel.BaseMutationViewModel
import kpt.core.base.ui.viewmodel.MutationMode
import kpt.core.data.banking.LoanRepository
import kpt.core.domain.calc.computeEmi
import kpt.core.model.banking.Loan
import kpt.core.model.banking.LoanCalcScenario
import kpt.core.model.banking.LoanKind
import kpt.core.model.emi.EmiResult
import kotlin.random.Random
import kotlin.time.Clock

/**
 * **Multi-step DraftSubmit showcase** — the B2 Loan Calculator Wizard.
 *
 * The wizard walks the user through five steps:
 *
 * | Step | Field           | Persistence              |
 * |------|-----------------|--------------------------|
 * | 1    | Principal       | snapshot → outbox        |
 * | 2    | Tenure (months) | snapshot → outbox        |
 * | 3    | APR             | snapshot → outbox        |
 * | 4    | Review (EMI)    | snapshot → outbox        |
 * | 5    | Name & save     | submit → LoanRepository  |
 *
 * The scenario id is the `uniqueKey` for the underlying `framework_submit_drafts`
 * row — N concurrent in-progress wizards may coexist, and resume from any device
 * session.
 *
 * On VM construction with a non-null [scenarioIdArg], the VM looks up any
 * persisted draft for that `(formKey, scenarioId)` pair and pre-fills the wizard
 * state — closing the app between steps and reopening returns to the last step
 * the user was on.
 *
 * The final step ([completeAndSave]) routes through the inherited
 * [BaseMutationViewModel.onSubmit] which calls [performSubmit] →
 * [LoanRepository.upsert]. On success the draft row is automatically marked
 * SUBMITTED.
 */
class LoanCalcWizardViewModel(
    outbox: SubmitOutbox<LoanCalcScenario>,
    private val repository: LoanRepository,
    private val scenarioIdArg: String?,
) : BaseMutationViewModel<LoanCalcScenario, Loan>(
    MutationMode.Draft(
        outbox = outbox,
        formKey = FORM_KEY,
        uniqueKey = scenarioIdArg ?: NEW_WIZARD_UNIQUE_KEY,
        autoSaveDraft = false,
    ),
) {

    /** Outbox kept as a VM-local handle so we can persist mid-wizard snapshots. */
    private val outboxHandle: SubmitOutbox<LoanCalcScenario> = outbox

    /** Effective unique-key used for this wizard's persisted drafts. */
    private val uniqueKey: String = scenarioIdArg ?: NEW_WIZARD_UNIQUE_KEY

    /** Wizard's in-memory form state — owns the per-step fields. */
    private val _formState = MutableStateFlow(
        LoanCalcScenario(scenarioId = scenarioIdArg ?: randomScenarioId()),
    )

    /** The form as it currently stands, across every step. */
    val formState: StateFlow<LoanCalcScenario> = _formState.asStateFlow()

    /**
     * Live preview of the EMI given the current inputs. Becomes meaningful at
     * step 4 (Review) once principal / rate / tenure are populated.
     */
    val preview: StateFlow<EmiResult> = _formState
        .map { s -> computeEmi(s.principal, s.ratePercent, s.tenureMonths) }
        .stateIn(
            viewModelScope,
            SharingStarted.WhileSubscribed(5_000),
            EmiResult(0.0, 0.0, 0.0),
        )

    /**
     * Becomes non-null once we've checked the outbox on first launch and found
     * a pre-existing in-progress draft to surface a "Continue / Discard?" prompt.
     */
    private val _resumeCandidate = MutableStateFlow<LoanCalcScenario?>(null)

    /**
     * A persisted draft found on first launch, or null. Non-null is what makes the screen offer "Continue / Discard".
     */
    val resumeCandidate: StateFlow<LoanCalcScenario?> = _resumeCandidate.asStateFlow()

    init {
        // On VM creation, look up any persisted draft for the wizard's uniqueKey and
        // surface it for the screen to render the Continue / Discard prompt.
        viewModelScope.launch {
            val pending = outboxHandle.getPendingByUniqueKey(FORM_KEY, uniqueKey)
            _resumeCandidate.value = pending?.payload
        }
    }

    /**
     * User confirmed "Continue" on the resume prompt — load the persisted snapshot
     * into the form state.
     */
    fun resumeDraft() {
        _resumeCandidate.value?.let { snapshot ->
            _formState.value = snapshot
            _resumeCandidate.value = null
        }
    }

    /**
     * User chose "Discard" — drop the persisted draft so the next session starts fresh.
     */
    fun discardSavedDraft() {
        viewModelScope.launch {
            outboxHandle.deleteByUniqueKey(FORM_KEY, uniqueKey)
            _resumeCandidate.value = null
        }
    }

    /**
     * Records a new principal into the wizard's form state, which auto-saves as a draft — so this write is what makes
     * the value survive process death.
     */
    fun onUpdatePrincipal(value: Double) {
        _formState.update { it.copy(principal = value) }
    }

    /**
     * Records a new rate. Feeds the live EMI preview, which becomes meaningful once principal and tenure are also set.
     */
    fun onUpdateRate(value: Double) {
        _formState.update { it.copy(ratePercent = value) }
    }

    /** Records a new tenure. */
    fun onUpdateTenure(value: Int) {
        _formState.update { it.copy(tenureMonths = value) }
    }

    /** Records a new name. The only field not used by the EMI preview. */
    fun onUpdateName(value: String) {
        _formState.update { it.copy(name = value) }
    }

    /**
     * Advance to the next step and persist the snapshot for resume-anywhere.
     * Safe to call after the last step — `currentStep` is clamped at [LAST_STEP].
     */
    fun goNext() {
        val next = (_formState.value.currentStep + 1).coerceAtMost(LAST_STEP)
        _formState.update { it.copy(currentStep = next) }
        persistSnapshot()
    }

    /**
     * Return to the previous step. Persists the snapshot so back-then-close still
     * resumes at the new step.
     */
    fun goBack() {
        val prev = (_formState.value.currentStep - 1).coerceAtLeast(1)
        _formState.update { it.copy(currentStep = prev) }
        persistSnapshot()
    }

    /**
     * Final step — convert the wizard payload to a [Loan] and submit through the
     * inherited [BaseMutationViewModel.onSubmit]. On success [performSubmit]
     * marks the outbox row SUBMITTED automatically.
     */
    fun completeAndSave() {
        val snapshot = _formState.value
        if (!snapshot.isReadyForSubmit()) return
        super.onSubmit(snapshot)
    }

    /**
     * Validation for the final wizard step — exposed `internal` so the screen can disable the
     * "Save as Loan" button visually (rather than silently no-op on tap, which leaves the user
     * unable to tell why nothing happened). Computed from the current [formState] snapshot.
     */
    internal fun LoanCalcScenario.isReadyForSubmit(): Boolean = principal > 0.0 &&
        ratePercent >= 0.0 &&
        tenureMonths > 0 &&
        name.isNotBlank()

    /** Persist the current wizard snapshot to the outbox keyed on [uniqueKey]. */
    private fun persistSnapshot() {
        val snapshot = _formState.value
        viewModelScope.launch {
            outboxHandle.saveByUniqueKey(FORM_KEY, uniqueKey, snapshot)
        }
    }

    override suspend fun performSubmit(payload: LoanCalcScenario): Loan {
        val nowMs = Clock.System.now().toEpochMilliseconds()
        val emi = computeEmi(payload.principal, payload.ratePercent, payload.tenureMonths).emi
        val loan = Loan(
            id = payload.scenarioId,
            name = payload.name.ifBlank { "Scenario" },
            kind = LoanKind.PERSONAL,
            principal = payload.principal,
            principalRemaining = payload.principal,
            annualRatePercent = payload.ratePercent,
            tenureMonths = payload.tenureMonths,
            monthsRemaining = payload.tenureMonths,
            monthlyPayment = emi,
            // Today + 30 d is a reasonable seed — user can edit in the loan tracker.
            nextDueDate = nextDueDateSeed(),
            totalPaid = 0.0,
            createdAtMs = nowMs,
            updatedAtMs = nowMs,
        )
        repository.upsert(loan)
        // Clear the draft so the NEXT wizard entry doesn't rehydrate the saved-and-done
        // snapshot (with currentStep=5 + a now-blank-feeling name field) — that was the
        // "Save as Loan does nothing" reproduction: a stale PENDING draft pinned the form
        // to step 5 with a cleared name, so the validator-gated Save button stayed disabled.
        outboxHandle.deleteByUniqueKey(FORM_KEY, uniqueKey)
        // Reset the form to a fresh scenario so the screen reflects a "saved → restart" state
        // rather than continuing to render the prior wizard's review fields.
        _formState.value = LoanCalcScenario(scenarioId = randomScenarioId())
        return loan
    }

    private fun nextDueDateSeed(): LocalDate {
        // Trivial seed used only as initial value; not security-sensitive.
        return LocalDate(YEAR_SEED, MONTH_SEED, DAY_SEED)
    }

    /** Outbox key and step bounds. */
    companion object {
        /** Namespaces this wizard's drafts in the outbox, so another form's draft is never resumed here. */
        const val FORM_KEY: String = "loan_calc_wizard"

        /** The final step index — what "next" stops at and what submit requires. */
        const val LAST_STEP: Int = 5

        /** Sentinel uniqueKey when starting a brand-new wizard with no scenarioId. */
        private const val NEW_WIZARD_UNIQUE_KEY: String = "new"

        // Plain seed values for the nextDueDate placeholder — non-cryptographic.
        private const val YEAR_SEED: Int = 2026
        private const val MONTH_SEED: Int = 6
        private const val DAY_SEED: Int = 1

        private fun randomScenarioId(): String {
            val chars = ('a'..'z') + ('0'..'9')
            return (1..16).map { chars[Random.nextInt(chars.size)] }.joinToString("")
        }
    }
}
